import React from 'react';
import { Link } from 'react-router-dom';
import AddressLink from './AddressLink';
import { getPercentageColor } from '../utils/riskUtils';

interface TokenDetails {
  mint: string;
  name: string;
  symbol: string;
  uri: string;
  creator: string;
  creatorNetwork: string;
  createdAt: string;
  description: string;
  telegram: string;
  twitter: string;
  website: string;
  decimals?: number;
  networkPercentage: string;
  riskLevel: string;
  networkBundles: Array<{
    address: string;
    name: string;
    percentOwnership: number;
    riskLevel: string;
    accounts: Array<{
      walletAddress: string;
      tokenAmount: string;
      percentage: number;
      topFundingSource: string;
      tokenAccountAddress: string;
      oldestTx: string;
      riskLevel: string;
      label: string;
      tags: string[];
    }>;
  }>;
  individualHolders: Array<{
    walletAddress: string;
    tokenAmount: string;
    percentage: number;
    topFundingSource: string;
    fundingSource: string;
    exchange: string;
    tokenAccountAddress: string;
    oldestTx: string;
    riskLevel: string;
    isLiquidityPool?: boolean;
    label: string;
    tags: string[];
    figureKey?: string | null;
    figureName?: string | null;
    figureConfidence?: 'confirmed' | 'probable';
    streamflowLocked?: string;
    streamflowLockedIn48h?: string;
    tibaneStaked?: string;
    multisigInfo?: {
      multisig: string;
      threshold: number;
      memberCount: number;
    };
  }>;
  uniqueWalletCount: number;
  totalAccounts: number;
  topNetworkOwnership: number;
}

interface HoldersTableProps {
  tokenDetails: TokenDetails;
}

const HoldersTable: React.FC<HoldersTableProps> = ({ tokenDetails }) => {
  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address);
  };

  const getAdjustedTokenAmount = (amount: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return num;

    // Use decimals if available, otherwise assume 1e6 (10^6)
    const decimals = tokenDetails.decimals ?? 6;
    return num / Math.pow(10, decimals);
  };

  const formatTokenAmount = (amount: string) => {
    const adjustedAmount = getAdjustedTokenAmount(amount);
    if (isNaN(adjustedAmount)) return amount;

    if (adjustedAmount >= 1e12) return (adjustedAmount / 1e12).toFixed(1) + 'T';
    if (adjustedAmount >= 1e9) return (adjustedAmount / 1e9).toFixed(1) + 'B';
    if (adjustedAmount >= 1e6) return (adjustedAmount / 1e6).toFixed(1) + 'M';
    if (adjustedAmount >= 1e3) return (adjustedAmount / 1e3).toFixed(1) + 'K';
    return adjustedAmount.toLocaleString();
  };

  const getRankColor = (index: number, percentage: number) => {
    if (index === 0 && percentage > 10) return 'crypto-red';
    if (percentage > 5) return 'crypto-yellow';
    return 'gray-600';
  };

  const formatWalletAge = (oldestTx: string) => {
    if (!oldestTx) return 'Unknown';

    const txDate = new Date(oldestTx);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - txDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 365) return `${diffDays}d`;
    if (diffDays < 365*3) return `${Math.floor(diffDays / 30)}mo`;
    return `${Math.floor(diffDays / 365)}y`;
  };



  // Filter out liquidity pool holders and use the remaining individual holders
  const nonLiquidityHolders = tokenDetails.individualHolders.filter(holder => !holder.isLiquidityPool);
  const sortedHolders = [...nonLiquidityHolders]
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 10); // Show top 10 holders

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Individual Holders</h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-400">Showing top {sortedHolders.length} holders</span>
        </div>
      </div>

      <div className="bg-crypto-gray border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-crypto-dark border-b border-gray-700">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Rank</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">% Held</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Token Amount</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400 hidden lg:table-cell">Wallet Age</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400 hidden lg:table-cell">Funding Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedHolders.map((holder, index) => {
                const rankColor = getRankColor(index, holder.percentage);

                return (
                  <tr key={holder.walletAddress} className="hover:bg-crypto-dark/50 transition-colors">
                    <td className="p-4">
                      <span className={`w-6 h-6 bg-${rankColor} rounded-full flex items-center justify-center text-xs font-bold text-white`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        {/* Address stays visible + linked to wallet details; the
                            figure (if any) follows in parens, linked to the figure
                            page. Probable = amber "?" lead (e.g. a GMGN vendor label). */}
                        <AddressLink
                          address={holder.walletAddress}
                          chain={(tokenDetails as any).chain}
                          className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                        >
                          {holder.label || (holder.walletAddress ? `${holder.walletAddress.slice(0, 6)}...${holder.walletAddress.slice(-4)}` : 'Unknown')}
                        </AddressLink>
                        {holder.figureName && holder.figureKey && (
                          <span className="text-xs text-gray-500">
                            (
                            <Link
                              to={`/figures/${holder.figureKey}`}
                              className={`font-semibold hover:underline inline-flex items-center gap-1 ${holder.figureConfidence === 'probable' ? 'text-amber-400' : 'text-crypto-accent'}`}
                              title={holder.figureConfidence === 'probable' ? `Probable identity (unconfirmed lead) — ${holder.figureName}` : `Known figure: ${holder.figureName}`}
                            >
                              <i className={`fas text-[10px] ${holder.figureConfidence === 'probable' ? 'fa-user-tag text-amber-400' : 'fa-user-check text-crypto-green'}`} />
                              {holder.figureName}{holder.figureConfidence === 'probable' ? ' ?' : ''}
                            </Link>
                            )
                          </span>
                        )}
                        {holder.streamflowLocked && holder.streamflowLocked !== '0' && (
                          <span className="text-blue-400" title="Streamflow locked tokens">
                            <i className="fas fa-lock text-xs"></i>
                          </span>
                        )}
                        {holder.tibaneStaked && holder.tibaneStaked !== '0' && (
                          <span className="text-purple-400" title="Staked via Tibane Labs">
                            <i className="fas fa-coins text-xs"></i>
                          </span>
                        )}
                        {holder.tags?.includes('multisig') && (
                          <span
                            className="text-amber-400 inline-flex items-center gap-1"
                            title={
                              holder.multisigInfo
                                ? `Squads multisig — ${holder.multisigInfo.threshold}-of-${holder.multisigInfo.memberCount} signers required`
                                : 'Squads multisig — shared-control wallet'
                            }
                          >
                            <i className="fas fa-shield-halved text-xs"></i>
                            {holder.multisigInfo && (
                              <span className="text-[10px] font-mono">
                                {holder.multisigInfo.threshold}-of-{holder.multisigInfo.memberCount}
                              </span>
                            )}
                          </span>
                        )}
                        {/* GMGN wallet labels (from wallets.tags / holder.tags) */}
                        {holder.tags?.includes('kol') && (
                          holder.label?.startsWith('@') ? (
                            <a
                              href={`https://x.com/${holder.label.slice(1)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink-400 hover:text-pink-300 inline-flex items-center"
                              title={`GMGN: KOL — ${holder.label} on X`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="fas fa-crown text-xs"></i>
                            </a>
                          ) : (
                            <span className="text-pink-400" title="GMGN: KOL wallet">
                              <i className="fas fa-crown text-xs"></i>
                            </span>
                          )
                        )}
                        {/* FOMO (fomo.family) social-trading account — label is the FOMO handle */}
                        {holder.tags?.includes('fomo_trader') && (
                          <span
                            className="text-orange-400"
                            title={`FOMO trader${holder.label?.startsWith('@') ? ` — ${holder.label} on fomo.family` : ''}`}
                          >
                            <i className="fas fa-fire text-xs"></i>
                          </span>
                        )}
                        {holder.tags?.includes('smart_money') && (
                          <span className="text-teal-400" title="GMGN: Smart money — statistically profitable track record">
                            <i className="fas fa-brain text-xs"></i>
                          </span>
                        )}
                        {holder.tags?.includes('sniper') && (
                          <span className="text-red-400" title="GMGN: Sniper on this token">
                            <i className="fas fa-crosshairs text-xs"></i>
                          </span>
                        )}
                        {holder.tags?.includes('bundler') && (
                          <span className="text-orange-400" title="GMGN: Bundler on this token">
                            <i className="fas fa-layer-group text-xs"></i>
                          </span>
                        )}
                        <button
                          onClick={() => copyToClipboard(holder.walletAddress || '')}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <i className="fas fa-copy text-xs"></i>
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      {/* Risk-level coloring preserved here now that the Risk Level column is gone */}
                      <span className={`font-bold ${
                        holder.riskLevel === 'HIGH' || holder.riskLevel === 'EXTREME' ? 'text-crypto-red' :
                        holder.riskLevel === 'MEDIUM' ? 'text-crypto-yellow' :
                        getPercentageColor(holder.percentage || 0)
                      }`} title={holder.riskLevel ? `${holder.riskLevel} risk` : undefined}>
                        {(holder.percentage || 0) < 0.1 ? (holder.percentage || 0).toFixed(3) : (holder.percentage || 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-white font-medium">{formatTokenAmount(holder.tokenAmount)}</span>
                      {holder.tibaneStaked && holder.tibaneStaked !== '0' && (
                        <div className="text-xs text-purple-400 mt-0.5">
                          {(() => {
                            const staked = parseFloat(holder.tibaneStaked);
                            const total = parseFloat(holder.tokenAmount);
                            const pct = total > 0 ? (staked / total * 100) : 0;
                            return `${pct.toFixed(0)}% staked`;
                          })()}
                        </div>
                      )}
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className="text-white font-medium">{formatWalletAge(holder.oldestTx)}</span>
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className="text-white font-medium">{holder.exchange || 'Unknown'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-700 flex items-center justify-between">
          <span className="text-sm text-gray-400">Showing {sortedHolders.length} of {nonLiquidityHolders.length} individual holders</span>
        </div>
      </div>
    </section>
  );
};

export default HoldersTable;
